<script lang="ts">
	import Skeleton from '$lib/components/primitives/Skeleton.svelte';
</script>

<section class="hero">
	<div class="container">
		<div class="hero__content">
			<!-- Title skeleton -->
			<div class="hero__title-skeleton">
				<Skeleton width="90%" height="3.5rem" rounded />
				<Skeleton width="70%" height="3.5rem" rounded />
			</div>

			<!-- Subtitle skeleton -->
			<div class="hero__subtitle-skeleton">
				<Skeleton width="85%" height="1.5rem" lines={3} spacing="0.5rem" />
			</div>

			<!-- CTA button skeleton -->
			<div class="hero__cta-skeleton">
				<Skeleton width="180px" height="48px" rounded />
			</div>

			<!-- Features skeleton -->
			<div class="hero__features-skeleton">
				{#each Array(4) as _}
					<div class="hero__feature-skeleton">
						<Skeleton width="18px" height="18px" circle />
						<Skeleton width="120px" height="1rem" />
					</div>
				{/each}
			</div>
		</div>

		<!-- Visual placeholder skeleton -->
		<div class="hero__visual-skeleton">
			<div class="hero__placeholder-skeleton">
				<div class="hero__placeholder-header">
					<Skeleton width="12px" height="12px" circle />
					<Skeleton width="12px" height="12px" circle />
					<Skeleton width="12px" height="12px" circle />
				</div>
				<div class="hero__placeholder-content">
					<Skeleton width="60%" height="20px" />
					<Skeleton width="70%" height="12px" />
					<Skeleton width="100%" height="12px" />
					<Skeleton width="100%" height="12px" />
					<Skeleton width="70%" height="12px" />
				</div>
			</div>
		</div>
	</div>
</section>

<style>
	.hero {
		position: relative;
		padding-block: clamp(var(--space-16), 10vw, var(--space-32));
		min-height: 600px;
		overflow: hidden;
	}

	.hero::before {
		content: '';
		position: absolute;
		inset: 0;
		background: radial-gradient(
			ellipse at top,
			hsl(var(--brand-h) var(--brand-s) var(--brand-l) / 0.05),
			transparent 70%
		);
		pointer-events: none;
	}

	.container {
		position: relative;
	}

	.hero__content {
		max-width: 800px;
		margin-inline: auto;
		text-align: center;
	}

	.hero__title-skeleton {
		margin-bottom: var(--space-6);
		display: flex;
		flex-direction: column;
		gap: var(--space-2);
		align-items: center;
	}

	.hero__subtitle-skeleton {
		max-width: 60ch;
		margin-inline: auto;
		margin-bottom: var(--space-10);
	}

	.hero__cta-skeleton {
		display: flex;
		justify-content: center;
		margin-bottom: var(--space-16);
	}

	.hero__features-skeleton {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
		gap: var(--space-4);
		margin-bottom: var(--space-16);
		min-height: 52px;
	}

	.hero__feature-skeleton {
		display: flex;
		align-items: center;
		gap: var(--space-2);
		padding: var(--space-3);
		background: var(--bg-secondary);
		border-radius: var(--radius-md);
		border: 1px solid var(--border-light);
		min-height: 52px;
	}

	.hero__visual-skeleton {
		margin-top: var(--space-16);
	}

	.hero__placeholder-skeleton {
		max-width: 600px;
		margin-inline: auto;
		background: var(--bg-elevated);
		border: 1px solid var(--border-light);
		border-radius: var(--radius-lg);
		box-shadow: var(--shadow-2xl);
		overflow: hidden;
		aspect-ratio: 16 / 10;
		min-height: 300px;
	}

	.hero__placeholder-header {
		display: flex;
		gap: var(--space-2);
		padding: var(--space-3);
		background: var(--bg-secondary);
		border-bottom: 1px solid var(--border-light);
	}

	.hero__placeholder-content {
		padding: var(--space-8);
		display: flex;
		flex-direction: column;
		gap: var(--space-4);
	}

	/* Responsive */
	@media (max-width: 768px) {
		.hero__features-skeleton {
			grid-template-columns: 1fr;
		}
	}
</style>
